import { getClinicalSignificanceString, getGenotypeString, QueryVariantWithClinvar } from '../../types/genomic';
import { styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, Typography, Box } from "@mui/material";
import { theme } from '@/theme';
import ACMGChips from './ACMGChips';

const StyledTableContainer = styled(TableContainer)({
    overflow: 'hidden',
    width: '100%',
    margin: 0,
    padding: 0
});

const StyledTable = styled(Table)({
    minWidth: 650,
    width: '100%',
    tableLayout: 'fixed',
    fontSize: '8pt',
    '@media print': {

    }
});

const StyledTableCell = styled(TableCell)({
    borderBottom: `1px solid ${theme.palette.divider}`,
    maxWidth: '200px',
    wordBreak: 'break-word',
    padding: '8px',
    fontSize: '8pt',
    wordWrap: 'break-word',
    'p': {
        margin: 0,
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        hyphens: 'auto',
        maxWidth: '200px',
        color: theme.palette.text.primary,
    }
});

const LocationWithReference = styled(Box)({
    color: theme.palette.text.primary,
    marginBottom: '10px',
    fontSize: '8pt',

    '@media print': {
    }
});

const HeaderCell = styled(StyledTableCell)({
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    fontSize: '8pt',
    fontWeight: 700,
    '@media print': {
      
    }
});

const BodyRow = styled(TableRow)({
    '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
    '@media print': {
        '&:nth-of-type(even)': {
            backgroundColor: '#f5f5f5',
        },
        '&:hover': {
            backgroundColor: 'transparent',
        },
    }
});



const ConditionText = styled(Typography)({
    margin: 0,
    lineHeight: '1.4',
    fontSize: '8pt',
    color: theme.palette.text.primary,
    '@media print': {
    }
});

const ConditionStack = styled(Stack)({
    gap: '4px',
});


const columns = ['Gene', 'Variant', 'Position', 'Zygosity', 'Condition', 'ACMG Criteria', 'Pathogenicity']

const VariantOverviewTable = (
    {
        variants
    }: {
        variants: QueryVariantWithClinvar[]
    }
) => {


    
    return (
        <StyledTableContainer>
            <StyledTable>
                <TableHead>
                    <TableRow>
                        {columns.map((column) => <HeaderCell key={column}>{column}</HeaderCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {variants.map((variant, index) => (
                        <BodyRow key={index}>
                            <StyledTableCell>{variant.clinvar.allele.GeneList?.Gene?.map(gene => gene.Symbol).join(', ') || ''}</StyledTableCell>
                            <StyledTableCell>{variant.clinvar.allele.Name}</StyledTableCell>
                            <StyledTableCell>
                                {variant.clinvar.allele.Location.SequenceLocation.map((location, index) => (
                                    <LocationWithReference key={index}>
                                        {location.Chr}:{location.start}-{location.stop}<br />
                                        {location.referenceAlleleVCF} / {location.alternateAlleleVCF} <br />
                                        ({location.Assembly}) 
                                    </LocationWithReference>
                                ))}
                            </StyledTableCell>
                            <StyledTableCell style={{ wordBreak: 'revert' }}>{getGenotypeString(variant.genotype)}</StyledTableCell>
                            <StyledTableCell>
                                {(() => {
                                    const conditions = variant.clinvar.conditions;
                                    const filteredConditions = conditions.filter(condition => condition !== 'not specified');
                                    const visibleConditions = filteredConditions.slice(0, 2);
                                    const remainingCount = filteredConditions.length - 2;


                                    if (filteredConditions.length === 0) {
                                        return <ConditionText></ConditionText>;
                                    }

                                    return (
                                        <ConditionStack>
                                            {visibleConditions.map((condition, index) => (
                                                <ConditionText key={index}>
                                                    {condition}
                                                </ConditionText>
                                            ))}
                                            {remainingCount > 0 && (
                                                <span>
                                                    +{remainingCount} more
                                                </span>
                                            )}
                                                     
                                        </ConditionStack>
                                    );
                                })()}
                            </StyledTableCell>
                            <StyledTableCell>
                                <ACMGChips acmg={variant.clinvar.acmg} />
                            </StyledTableCell>
                            <StyledTableCell>{getClinicalSignificanceString(variant.clinvar.classification)}</StyledTableCell>
                        </BodyRow>
                    ))}
                </TableBody>
            </StyledTable>
        </StyledTableContainer>
    );
};

export default VariantOverviewTable;
